import {
  resolveBrowserHref,
  resolveCuratedImage,
  type BrowserRef,
  type NewsItem,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';

/** Uma imagem da galeria, como o `ObjectListWidget` grava. */
export interface SubHomeGaleriaItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
  /** Legenda opcional, exibida abaixo da imagem. */
  caption?: string;
  href?: BrowserRef[];
  external?: boolean;
}

/** Dados do bloco `mpfSubHomeGaleria`. */
export interface SubHomeGaleriaData {
  title?: string;
  items?: SubHomeGaleriaItem[];
}

/** View-model de uma imagem. */
export interface SubHomeGaleriaCard {
  key: string;
  /** Posição em `items` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  alt: string;
  caption?: string;
  href?: string;
  external?: boolean;
  item: SubHomeGaleriaItem;
}

/** Título padrão da faixa, quando o editor não escreve um. */
export const SUBHOME_GALERIA_TITLE = 'Carrossel Imagens';

/** Escala da imagem do card (329px de largura no design). */
export const SUBHOME_GALERIA_SCALE = 'preview';

/**
 * Piso e teto de itens, como na anotação do Figma ("até 10 itens de
 * carrossel, mínimo de 3").
 *
 * O piso é um AVISO no editor, não uma trava: quem está montando a página
 * passa necessariamente por 1 e por 2 itens antes de chegar ao terceiro, e
 * fazer a faixa sumir no meio do caminho é pior do que exibi-la incompleta.
 * O teto, esse sim, corta no render.
 */
export const SUBHOME_GALERIA_MIN_ITEMS = 3;
export const SUBHOME_GALERIA_MAX_ITEMS = 10;

/** Quantas imagens cabem na maior largura — só decide o `loop` do Swiper. */
export const SUBHOME_GALERIA_SLIDES = 3;

export const toSubHomeGaleriaCards = (
  items?: SubHomeGaleriaItem[],
): SubHomeGaleriaCard[] =>
  (items ?? [])
    .slice(0, SUBHOME_GALERIA_MAX_ITEMS)
    .map((item, index) => ({
      key: item['@id'] ?? `imagem-${index}`,
      sourceIndex: index,
      alt: item.alt || item.caption || '',
      caption: item.caption || undefined,
      href: resolveBrowserHref(item.href),
      external: item.external,
      item,
    }))
    // Item sem imagem não vira slide: um card vazio no meio do carrossel é
    // só um buraco. A legenda sozinha não sustenta a faixa.
    .filter(
      (card) => !!resolveCuratedImage(card.item.image, SUBHOME_GALERIA_SCALE),
    );

/** Props de imagem do card, prontas para o `<Image>` do Volto. */
export const getSubHomeGaleriaImageProps = (
  card: SubHomeGaleriaCard,
  scale: string = SUBHOME_GALERIA_SCALE,
): { item: NewsItem } | { src: string } | null =>
  resolveCuratedImage(card.item.image, scale);
